import { ComboboxItem, Stack, Text, FileButton, Button } from '@mantine/core';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { v4 as uuidv4 } from 'uuid';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { EntityWithFile, UploadedFile } from '../hooks/file-uploads/types';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import FilesTable from './files-table/FilesTable';
import FileStatusPill from './FileStatusPill';
import { useEffect, useMemo, useState } from 'react';
import { useDetectedEntitiesStore } from '../stores/useDetectedEntitiesStore';
import { DropZone } from './drop-zone/DropZone';
import useDataUploadModels from 'src/data-capture/hooks/templates/useDataUploadModels';
import {
  FileWithTemplate,
  UploadFilesModal,
} from 'src/data-capture/file-management/drop-zone/UploadFilesModal';
import { dataUploadFileProperties, Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { useNavigation } from '@refinedev/core';
import { IconArrowRight, IconFolder, IconPlus } from '@tabler/icons-react';
import MoveFileModal from './files-table/MoveFileModal';
import { useDisclosure } from '@mantine/hooks';
import FileUploadLayout from './FileUploadLayout';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import EmptyView from './components/EmptyView';

export function AddDocumentsPage() {
  const { clientId } = useCurrentClient();
  const clearExpiredFiles = useDetectedEntitiesStore(state => state.clearExpiredFiles);
  const { push } = useNavigation();
  const [searchValue, setSearchValue] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [uploadModalOpened, { open: openUploadModal, close: closeUploadModal }] =
    useDisclosure(false);

  const selectData = [
    { value: 'BANK_STATEMENT', label: 'Bank statement' },
    { value: 'LETTER_OF_CORRESPONDENCE', label: 'Letter of correspondence' },
    { value: 'UTILITY_BILL', label: 'Utility bill' },
    { value: 'FACT_FIND', label: 'Fact find' },
    { value: 'IDENTIFICATION_DOCUMENT', label: 'Identification document' },
    { value: 'MEETING_NOTES', label: 'Meeting notes' },
    { value: 'AUDIO', label: 'Audio' },
  ];
  const [documentTypeChoice] = useState<ComboboxItem>(selectData[0]);

  useEffect(() => {
    clearExpiredFiles();
  }, []);

  const {
    filesToAdd,
    deleteFile,
    setFileResolved,
    refetchFiles,
    moveFile,
    isLoading: isLoadingFiles,
    error,
  } = useFileUploads({
    autoRefreshProcessing: true,
  });
  const isLoading = isLoadingFiles;

  const { files: models } = useDataUploadModels();
  const { uploadFiles } = useFileUploads();

  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  const handleDeleteFile = async (file: EntityWithFile) => {
    try {
      await deleteFile(file.id);
    } catch (error) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        error,
        { clientId, fileId: file.id },
      );
    }
  };

  const handleSetAsResolved = async (file: UploadedFile, isResolved: boolean = true) => {
    try {
      Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_SET_RESOLVED, dataUploadFileProperties(file));
      await setFileResolved(file, isResolved);
    } catch (error) {
      notifyAndLogError(
        'Failed to mark file as reviewed',
        'An error occurred while trying to mark the file as reviewed. Please try again later.',
        error,
        { clientId, fileId: file.id },
      );
    }
  };

  const handleViewFile = async (uploadedFile: UploadedFile, anchor?: HTMLAnchorElement) => {
    try {
      if (anchor) {
        const link = await createLink(uploadedFile.file.id);
        anchor.href = link.url;
        anchor.download = uploadedFile.file.fileName || 'download';
        anchor.target = '_blank';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } else {
        const link = await createLink(uploadedFile.file.id);
        window.open(link.url, '_blank');
      }
    } catch (error) {
      notifyAndLogError(
        'Failed to open file',
        'An error occurred while trying to open the file. Please try again later.',
        error,
        { clientId, fileId: uploadedFile.file.id },
      );
    }
  };

  const showUploadModal = (files: File[]) => {
    setSelectedFiles(files);
    openUploadModal();
  };

  const handleFilesUpload = async (filesWithTemplate: FileWithTemplate[]) => {
    try {
      const fileUploadInputs = filesWithTemplate.map(({ file, modelId, documentDate }) => ({
        file,
        modelId,
        documentDate,
      }));

      const uploadedFiled = await uploadFiles(fileUploadInputs);
      await handleFilesUploaded(uploadedFiled.map(f => f.data));
      closeUploadModal();
    } catch (error) {
      notifyAndLogError(
        'Failed to upload files',
        'An error occurred while uploading the files. Please try again later.',
        error,
        { clientId, fileCount: filesWithTemplate.length },
      );
      await refetchFiles();
      push(`/client/${clientId}/document/add`);
    }
  };

  const handleFilesUploaded = async (uploadedFiles: UploadedFile[]) => {
    try {
      uploadedFiles.forEach(file => {
        Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_UPLOADED, dataUploadFileProperties(file));
      });

      await refetchFiles();
      push(`/client/${clientId}/document/add`);
    } catch (error) {
      notifyAndLogError(
        'Failed to process uploaded files',
        'An error occurred while processing the uploaded files. Please try again later.',
        error,
        { clientId, fileCount: uploadedFiles.length },
      );
    }
  };

  const handleFileDocument = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_DOCUMENT_FILED, {
        ...dataUploadFileProperties(file),
        parentId,
      });
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { clientId, fileId: file.id, parentId },
      );
    }
  };

  const filteredFiles = useMemo(() => {
    if (!searchValue) return filesToAdd;
    return filesToAdd.filter(file =>
      file.file.fileName.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [filesToAdd, searchValue]);

  const getPrimaryAction = (file: UploadedFile) => {
    if (!file.isResolved) {
      return {
        type: 'link' as const,
        label: 'Process data',
        variant: 'primary' as const,
        icon: IconArrowRight,
        onClick: () => {
          Mixpanel.track(
            MixpanelEvents.DATA_UPLOAD_FILE_PROCESS_CLICK,
            dataUploadFileProperties(file),
          );
        },
        getLink: (file: UploadedFile) =>
          `/client/${clientId}/detected-entities?fileId=${file.id}&fileName=${file.file.fileName}&guid=${uuidv4()}`,
        condition: (file: UploadedFile) =>
          !file.backgroundProcess || file.backgroundProcess.status === 'SUCCESS',
      };
    }

    if (file.isResolved && !file.file.isFiled) {
      return {
        type: 'action' as const,
        label: 'File document',
        variant: 'secondary' as const,
        icon: IconFolder,
        onClick: handleFileDocument,
      };
    }

    return undefined;
  };

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  return (
    <>
      {filteredFiles.length === 0 ? (
        <FileUploadLayout title="Add documents">
          <>
            <EmptyView
              title="Keep all documents securely and in one place"
              subtitle="Siesta AI will scan and look for data that you use to populate the fields on the system. Documents can be used for Siesta AI reports either for context and/or as information for including."
              ctaText="Upload new"
              ctaIcon={IconPlus}
              customCta={
                <FileButton
                  onChange={showUploadModal}
                  multiple
                  accept=".png,.jpg,.jpeg,.pdf,.docx,.txt,.mp3,.mp4,.wav,.aac,.flac,.ogg"
                >
                  {props => (
                    <Button
                      {...props}
                      variant="primary"
                      size="md"
                      leftSection={<IconPlus size={16} />}
                    >
                      Upload new
                    </Button>
                  )}
                </FileButton>
              }
            />
          </>
        </FileUploadLayout>
      ) : (
        <FileUploadLayout
          title="Add documents"
          showSearch
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        >
          <Stack gap="xxl">
            <DropZone onFilesSelected={showUploadModal} documentType={documentTypeChoice} />

            <FilesTable<UploadedFile>
              entitiesWithFile={filteredFiles}
              isLoading={isLoading}
              primaryAction={getPrimaryAction}
              additionalColumns={[
                {
                  label: 'AI model',
                  valueBuilder: f => (
                    <Text fw={400} size="sm">
                      {f.dataUploadModel?.shortName ?? f.dataUploadModel?.displayName}
                    </Text>
                  ),
                },
                {
                  label: 'Status',
                  valueBuilder: f => {
                    if (f.isResolved && !f.file.isFiled) {
                      return <FileStatusPill status="UNFILED" />;
                    }
                    if (!f.backgroundProcess?.status) {
                      return <></>;
                    }
                    return <FileStatusPill status={f.backgroundProcess.status} />;
                  },
                },
              ]}
              actions={(file: UploadedFile) => {
                if (file.isResolved) {
                  return [
                    { label: 'Download document', action: handleViewFile },
                    { type: 'divider' },
                    {
                      label: 'Set as not reviewed',
                      action: file => handleSetAsResolved(file, false),
                    },
                    { label: 'Delete', action: handleDeleteFile },
                  ];
                } else {
                  return [
                    { label: 'Download document', action: handleViewFile },
                    { type: 'divider' },
                    { label: 'Set as reviewed', action: file => handleSetAsResolved(file, true) },
                    { label: 'Delete', action: handleDeleteFile },
                  ];
                }
              }}
            />
          </Stack>
        </FileUploadLayout>
      )}

      <UploadFilesModal
        opened={uploadModalOpened}
        close={closeUploadModal}
        files={selectedFiles}
        dataUploadModels={models}
        onSubmit={handleFilesUpload}
      />

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          onFileMoved={handleMoveFile}
        />
      )}
    </>
  );
}
